import React, { useCallback, useMemo, useState } from 'react';
import { Alert, Button, ButtonGroup, Form, Pagination, Spinner, Table } from 'react-bootstrap';
import { FiEdit2, FiPlus, FiRefreshCw, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { CrudService, ListParams } from '../../services';
import { usePaginatedResource } from '../../hooks';
import { ResourceSearch } from './ResourceSearch';

export interface ResourceColumn<T> {
  header: string;
  accessor?: keyof T | string;
  render?: (item: T) => React.ReactNode;
  className?: string;
  align?: 'left' | 'center' | 'right';
}

export type ResourceListPageSetParams = (next: ListParams | ((prev: ListParams) => ListParams)) => void;

export interface ResourceListFilterHelpers {
  params: ListParams;
  setParams: ResourceListPageSetParams;
  clearFilters: () => void;
}

export interface ResourceListPageProps<T extends { id?: number | string }> {
  title: string;
  description?: string;
  columns: ResourceColumn<T>[];
  service: CrudService<T>;
  createPath?: string;
  emptyMessage?: string;
  getEditPath?: (item: T) => string;
  onEdit?: (item: T) => void;
  onDelete?: (item: T) => Promise<void>;
  enableEdit?: boolean;
  enableDelete?: boolean;
  enableSearch?: boolean;
  searchPlaceholder?: string;
  searchParamKey?: string;
  filters?: (helpers: ResourceListFilterHelpers) => React.ReactNode;
  initialParams?: ListParams;
  renderRowActions?: (item: T) => React.ReactNode;
}

const resolveValue = <T,>(item: T, accessor?: keyof T | string) => {
  if (!accessor) return '';
  const path = accessor.toString().split('.');
  return path.reduce<unknown>((acc, key) => {
    if (acc && typeof acc === 'object' && key in acc) {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, item);
};

const numberFormatter = new Intl.NumberFormat('pt-BR');

const formatDisplayValue = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) return '—';

  if (typeof value === 'number') {
    return numberFormatter.format(value);
  }

  if (typeof value === 'string') {
    // Try date or datetime
    const isoDateLike = /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;
    if (isoDateLike.test(value)) {
      let date: Date;
      const hasTime = value.includes('T') || value.includes(' ');
      if (hasTime) {
        date = new Date(value);
      } else {
        const [year, month, day] = value.split('-').map((part) => Number(part));
        date = new Date(year, month - 1, day);
      }
      if (!Number.isNaN(date.getTime())) {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        if (hasTime) {
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          return `${day}/${month}/${year} ${hours}:${minutes}`;
        }
        return `${day}/${month}/${year}`;
      }
    }

    const numeric = Number(value);
    if (!Number.isNaN(numeric) && value.trim() !== '') {
      return numberFormatter.format(numeric);
    }

    return value;
  }

  return value as React.ReactNode;
};

export function ResourceListPage<T extends { id?: number | string }>({
  title,
  description,
  columns,
  service,
  createPath,
  emptyMessage = 'Nenhum registro encontrado.',
  getEditPath,
  onEdit,
  onDelete,
  enableEdit = true,
  enableDelete = true,
  enableSearch = true,
  searchPlaceholder = 'Buscar...',
  searchParamKey = 'search',
  filters,
  initialParams,
  renderRowActions,
}: ResourceListPageProps<T>) {
  const navigate = useNavigate();
  const paginatedOptions = useMemo(
    () => (initialParams ? { initialParams } : undefined),
    [initialParams],
  );
  const { data, loading, error, pagination, setPage, setPageSize, refresh, setParams, params } =
    usePaginatedResource<T>(service.list, paginatedOptions);
  const clearFilters = useCallback(() => {
    setParams((prev) => ({
      page: 1,
      page_size: prev.page_size,
      ...(initialParams ?? {}),
    }));
  }, [initialParams, setParams]);
  const [deletingId, setDeletingId] = useState<number | string | null>(null);
  const editHandler = enableEdit
    ? onEdit ||
      (getEditPath
        ? (item: T) => navigate(getEditPath(item))
        : createPath
        ? (item: T) => navigate(createPath, { state: { record: item } })
        : undefined)
    : undefined;
  const deleteHandler = useMemo(
    () =>
      enableDelete
        ? onDelete ||
          ((item: T) => (item.id ? service.remove(item.id) : Promise.resolve()))
        : undefined,
    [enableDelete, onDelete, service],
  );
  const showActions = Boolean(editHandler || deleteHandler || renderRowActions);

  // Calcula total de páginas usando count e pageSize
  // Mas valida com hasNext para garantir consistência com a API
  const calculatedPages = Math.ceil(pagination.total / Math.max(1, pagination.pageSize));
  const totalPages = Math.max(
    1,
    calculatedPages,
    // Se há next, garantir que temos pelo menos page + 1
    pagination.hasNext ? pagination.page + 1 : pagination.page,
  );

  const handleDelete = useCallback(
    async (item: T) => {
      if (!deleteHandler) return;
      if (!item.id) return;
      const confirmed = window.confirm('Deseja realmente excluir este registro?');
      if (!confirmed) return;
      setDeletingId(item.id);
      try {
        await deleteHandler(item);
        await refresh();
      } finally {
        setDeletingId(null);
      }
    },
    [deleteHandler, refresh],
  );

  const bodyContent = (
    // @ts-ignore React 19 JSX tipagem exige children único
    <div className="d-flex flex-column gap-4">
      <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start gap-3">
        <div>
          <h5 className="mb-1">{title}</h5>
          {description && <p className="text-muted mb-0">{description}</p>}
        </div>
        <div className="d-flex gap-2 align-items-center flex-wrap justify-content-start justify-content-lg-end w-100">
          {enableSearch && (
            <div
              className="flex-grow-1 flex-lg-grow-0"
              style={{ minWidth: '200px', maxWidth: '520px', width: '100%' }}
            >
              <ResourceSearch
                placeholder={searchPlaceholder}
                initialValue={(params[searchParamKey] as string) ?? ''}
                onSearch={(value) =>
                  setParams((prev) => ({
                    ...prev,
                    [searchParamKey]: value || undefined,
                    page: 1,
                  }))
                }
              />
            </div>
          )}
          <Button variant="outline-secondary" onClick={refresh}>
            <FiRefreshCw className="me-1" />
            <span className="d-none d-sm-inline">Atualizar</span>
          </Button>
          {createPath && (
            <Button onClick={() => navigate(createPath)}>
              <FiPlus className="me-2" />
              <span className="d-none d-sm-inline">Novo</span>
            </Button>
          )}
        </div>
      </div>

      {filters && (
        <div className="w-100 mb-3">
          {filters({ params, setParams, clearFilters })}
        </div>
      )}

      {error && (
        <Alert variant="danger" className="mb-0">
          Não foi possível carregar os dados. Tente novamente.
        </Alert>
      )}

      <div className="table-responsive">
        <Table hover className="align-middle">
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column.header} className={column.className}>
                  {column.header}
                </th>
              ))}
              {showActions && <th className="text-end">Ações</th>}
            </tr>
          </thead>
          <tbody>
            {!loading && data.length === 0 && (
              <tr>
                <td colSpan={columns.length + (showActions ? 1 : 0)} className="text-center text-muted py-4">
                  {emptyMessage}
                </td>
              </tr>
            )}

            {data.map((item, index) => (
              <tr key={(item as { id?: number | string }).id ?? index}>
                {columns.map((column, colIdx) => (
                  <td key={colIdx} className={column.className} style={{ textAlign: column.align || 'left' }}>
                    {column.render
                      ? column.render(item)
                      : formatDisplayValue(resolveValue(item, column.accessor))}
                  </td>
                ))}
                {showActions && (
                  <td className="text-end">
                    <div className="d-flex justify-content-end align-items-center gap-2 flex-wrap">
                      {renderRowActions && renderRowActions(item)}
                      {(editHandler || deleteHandler) && (
                        <ButtonGroup size="sm">
                          {editHandler && (
                            <Button variant="outline-primary" onClick={() => editHandler(item)}>
                              <FiEdit2 />
                            </Button>
                          )}
                          {deleteHandler && (
                            <Button
                              variant="outline-danger"
                              disabled={deletingId === item.id}
                              onClick={() => handleDelete(item)}
                            >
                              {deletingId === item.id ? (
                                <Spinner animation="border" size="sm" />
                              ) : (
                                <FiTrash2 />
                              )}
                            </Button>
                          )}
                        </ButtonGroup>
                      )}
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      {loading && (
        <div className="d-flex justify-content-center py-4">
          <Spinner animation="border" role="status" />
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Form.Group className="d-flex align-items-center gap-2 mb-0">
            <Form.Label className="mb-0 text-nowrap">Itens por página:</Form.Label>
            <Form.Select
              size="sm"
              style={{ width: 'auto' }}
              value={pagination.pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Form.Select>
          </Form.Group>
          <small className="text-muted">
            {(() => {
              const startItem = (pagination.page - 1) * pagination.pageSize + 1;
              const endItem = Math.min(pagination.page * pagination.pageSize, pagination.total);
              return `Exibindo ${startItem} - ${endItem} de ${pagination.total} itens`;
            })()}
          </small>
        </div>
        {totalPages > 1 && (
          <Pagination className="mb-0">
            <Pagination.Prev
              disabled={!pagination.hasPrevious}
              onClick={() => setPage(Math.max(1, pagination.page - 1))}
            />
            {(() => {
              const items: React.ReactNode[] = [];
              let lastPage = 0;
              for (let pageNumber = 1; pageNumber <= totalPages; pageNumber += 1) {
                const isEdge =
                  pageNumber === 1 ||
                  pageNumber === totalPages ||
                  Math.abs(pageNumber - pagination.page) <= 1;
                if (!isEdge) {
                  continue;
                }
                if (pageNumber - lastPage > 1) {
                  items.push(
                    <Pagination.Ellipsis key={`ellipsis-${pageNumber}`} disabled />,
                  );
                }
                items.push(
                  <Pagination.Item
                    key={pageNumber}
                    active={pageNumber === pagination.page}
                    onClick={() => setPage(pageNumber)}
                  >
                    {pageNumber}
                  </Pagination.Item>,
                );
                lastPage = pageNumber;
              }
              return items;
            })()}
            <Pagination.Next
              disabled={!pagination.hasNext}
              onClick={() => setPage(pagination.page + 1)}
            />
          </Pagination>
        )}
      </div>
    </div>
  );

  return React.createElement(
    'div',
    { className: 'card shadow-sm border-0' },
    React.createElement('div', { className: 'card-body' }, bodyContent),
  );
}
